<template>
  <div class="lb-page">
    <header class="lb-toolbar">
      <div class="lb-toolbar__left">
        <div class="lb-accent" />
        <div>
          <h1 class="lb-title">Telesales Leaderboard</h1>
          <p class="lb-subtitle">
            Ranked by successful sales minus returns
            <span v-if="organisationLabel" class="lb-subtitle__org">· {{ organisationLabel }}</span>
          </p>
        </div>
      </div>
      <div class="lb-toolbar__right">
        <div v-if="canSwitchOrg" class="lb-org-switch">
          <label class="lb-org-switch__label">Organisation</label>
          <AppDropdown
            v-model="selectedOrganisationId"
            :options="organisationOptions"
            :searchable="organisationOptions.length > 6"
            search-placeholder="Search organisations…"
            placeholder="Select organisation"
            @input="onOrgChange"
          />
        </div>
        <button
          type="button"
          class="lb-refresh"
          :disabled="isLoading"
          title="Refresh"
          aria-label="Refresh"
          @click="fetchLeaderboard"
        >
          <svg :class="['lb-refresh__icon', { 'is-spinning': isLoading }]" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
          </svg>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="lb-alert">
      {{ errorMessage }}
    </CAlert>

    <section class="lb-hero">
      <article :class="['lb-hero-card', 'lb-hero-card--week', { 'is-empty': !showInitialLoading && !employeeOfTheWeek }]">
        <div class="lb-hero-card__badge">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="7" />
            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
          </svg>
          <span>Employee of the Week</span>
        </div>
        <div v-if="showInitialLoading" class="lb-hero-card__body">
          <div class="lb-hero-card__avatar lb-hero-card__avatar--skeleton lb-shimmer-on-dark"></div>
          <div class="lb-hero-card__meta">
            <div class="lb-skeleton-bar lb-skeleton-bar--xl lb-shimmer-on-dark"></div>
            <div class="lb-skeleton-bar lb-skeleton-bar--sm lb-shimmer-on-dark"></div>
            <div class="lb-hero-card__stats">
              <div v-for="n in 3" :key="`hw-sk-${n}`" class="lb-stat">
                <div class="lb-skeleton-bar lb-skeleton-bar--md lb-shimmer-on-dark"></div>
                <div class="lb-skeleton-bar lb-skeleton-bar--xs lb-shimmer-on-dark"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="employeeOfTheWeek" class="lb-hero-card__body">
          <div
            class="lb-hero-card__avatar"
            :style="!photoUrl(employeeOfTheWeek) ? { background: avatarColor(employeeOfTheWeek) } : null"
          >
            <img
              v-if="photoUrl(employeeOfTheWeek)"
              :src="photoUrl(employeeOfTheWeek)"
              :alt="employeeOfTheWeek.name"
              class="lb-avatar__img"
              @error="onAvatarError(employeeOfTheWeek)"
            >
            <span v-else class="lb-avatar__initials">{{ initials(employeeOfTheWeek.name) }}</span>
            <span class="lb-hero-card__crown" aria-hidden="true">★</span>
          </div>
          <div class="lb-hero-card__meta">
            <div class="lb-hero-card__name">{{ employeeOfTheWeek.name }}</div>
            <div class="lb-hero-card__window">{{ formatRange(window.employee_of_the_week) }}</div>
            <div class="lb-hero-card__stats">
              <div class="lb-stat">
                <span class="lb-stat__value">{{ employeeOfTheWeek.score }}</span>
                <span class="lb-stat__label">Score</span>
              </div>
              <div class="lb-stat">
                <span class="lb-stat__value">{{ employeeOfTheWeek.successful_orders }}</span>
                <span class="lb-stat__label">Sales</span>
              </div>
              <div class="lb-stat">
                <span class="lb-stat__value">{{ formatPercent(employeeOfTheWeek.return_rate) }}</span>
                <span class="lb-stat__label">Return rate</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="lb-hero-card__empty">No qualifying sales last week.</div>
      </article>

      <article :class="['lb-hero-card', 'lb-hero-card--month', { 'is-empty': !showInitialLoading && !employeeOfTheMonth }]">
        <div class="lb-hero-card__badge">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
            <path d="M4 22h16" />
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
          </svg>
          <span>Employee of the Month</span>
        </div>
        <div v-if="showInitialLoading" class="lb-hero-card__body">
          <div class="lb-hero-card__avatar lb-hero-card__avatar--gold lb-hero-card__avatar--skeleton lb-shimmer-on-dark"></div>
          <div class="lb-hero-card__meta">
            <div class="lb-skeleton-bar lb-skeleton-bar--xl lb-shimmer-on-dark"></div>
            <div class="lb-skeleton-bar lb-skeleton-bar--sm lb-shimmer-on-dark"></div>
            <div class="lb-hero-card__stats">
              <div v-for="n in 3" :key="`hm-sk-${n}`" class="lb-stat">
                <div class="lb-skeleton-bar lb-skeleton-bar--md lb-shimmer-on-dark"></div>
                <div class="lb-skeleton-bar lb-skeleton-bar--xs lb-shimmer-on-dark"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="employeeOfTheMonth" class="lb-hero-card__body">
          <div
            class="lb-hero-card__avatar lb-hero-card__avatar--gold"
            :style="!photoUrl(employeeOfTheMonth) ? { background: avatarColor(employeeOfTheMonth) } : null"
          >
            <img
              v-if="photoUrl(employeeOfTheMonth)"
              :src="photoUrl(employeeOfTheMonth)"
              :alt="employeeOfTheMonth.name"
              class="lb-avatar__img"
              @error="onAvatarError(employeeOfTheMonth)"
            >
            <span v-else class="lb-avatar__initials">{{ initials(employeeOfTheMonth.name) }}</span>
            <span class="lb-hero-card__crown" aria-hidden="true">♛</span>
          </div>
          <div class="lb-hero-card__meta">
            <div class="lb-hero-card__name">{{ employeeOfTheMonth.name }}</div>
            <div class="lb-hero-card__window">{{ formatRange(window.employee_of_the_month) }}</div>
            <div class="lb-hero-card__stats">
              <div class="lb-stat">
                <span class="lb-stat__value">{{ employeeOfTheMonth.score }}</span>
                <span class="lb-stat__label">Score</span>
              </div>
              <div class="lb-stat">
                <span class="lb-stat__value">{{ employeeOfTheMonth.successful_orders }}</span>
                <span class="lb-stat__label">Sales</span>
              </div>
              <div class="lb-stat">
                <span class="lb-stat__value">{{ formatCurrency(employeeOfTheMonth.net_revenue) }}</span>
                <span class="lb-stat__label">Net revenue</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="lb-hero-card__empty">No qualifying sales last month.</div>
      </article>
    </section>

    <section class="lb-section">
      <div class="lb-tabs" role="tablist">
        <button
          v-for="tab in periodTabs"
          :key="tab.key"
          type="button"
          role="tab"
          :class="['lb-tab', { 'is-active': activePeriod === tab.key }]"
          :aria-selected="activePeriod === tab.key"
          @click="onPeriodChange(tab.key)"
        >
          <span class="lb-tab__label">{{ tab.label }}</span>
        </button>
      </div>
      <div v-if="window.current" class="lb-window">
        Showing {{ formatRange(window.current) }}
      </div>
    </section>

    <section v-if="showInitialLoading" class="lb-podium">
      <div
        v-for="n in 3"
        :key="`pd-sk-${n}`"
        :class="['lb-podium__slot', `lb-podium__slot--rank${n}`, 'lb-podium__slot--skeleton']"
      >
        <div class="lb-podium__rank lb-podium__rank--skeleton lb-shimmer-on-dark"></div>
        <div :class="['lb-podium__avatar', `lb-podium__avatar--rank${n}`, 'lb-podium__avatar--skeleton', 'lb-shimmer']"></div>
        <div class="lb-skeleton-bar lb-skeleton-bar--md lb-shimmer lb-podium__sk-name"></div>
        <div class="lb-skeleton-bar lb-skeleton-bar--xl lb-shimmer lb-podium__sk-score"></div>
        <div class="lb-skeleton-bar lb-skeleton-bar--sm lb-shimmer lb-podium__sk-detail"></div>
      </div>
    </section>

    <section v-else-if="podium.length" class="lb-podium">
      <div
        v-for="entry in podium"
        :key="entry.user_id"
        :class="['lb-podium__slot', `lb-podium__slot--rank${entry.rank}`]"
      >
        <div class="lb-podium__rank">#{{ entry.rank }}</div>
        <div
          :class="['lb-podium__avatar', `lb-podium__avatar--rank${entry.rank}`]"
          :style="!photoUrl(entry) ? { background: avatarColor(entry) } : null"
        >
          <img
            v-if="photoUrl(entry)"
            :src="photoUrl(entry)"
            :alt="entry.name"
            class="lb-avatar__img"
            @error="onAvatarError(entry)"
          >
          <span v-else class="lb-avatar__initials lb-podium__initials">{{ initials(entry.name) }}</span>
          <span v-if="entry.rank === 1" class="lb-podium__crown" aria-hidden="true">♛</span>
        </div>
        <div class="lb-podium__name">{{ entry.name }}</div>
        <div class="lb-podium__score">{{ entry.score }}</div>
        <div class="lb-podium__detail">
          {{ entry.successful_orders }} sales · {{ entry.returned_orders }} returns
        </div>
      </div>
    </section>

    <section class="lb-section lb-charts">
      <div class="lb-chart-card">
        <div class="lb-chart-card__head">
          <h3>Top performer scores</h3>
          <span class="lb-chart-card__hint">{{ activePeriod === 'month' ? 'Last month' : 'Last week' }}</span>
        </div>
        <div class="lb-chart-card__body">
          <ChartSkeleton v-if="showInitialLoading" variant="bar" />
          <CChartBar v-else-if="scoreChartData.datasets[0].data.length" :datasets="scoreChartData.datasets" :labels="scoreChartData.labels" :options="barOptions" style="height: 280px;" />
          <div v-else class="lb-empty">No data for this period.</div>
        </div>
      </div>
      <div class="lb-chart-card">
        <div class="lb-chart-card__head">
          <h3>Top 3 trend</h3>
          <span class="lb-chart-card__hint">{{ activePeriod === 'month' ? 'Last 8 weeks' : 'Last 7 days' }}</span>
        </div>
        <div class="lb-chart-card__body">
          <ChartSkeleton v-if="showInitialLoading" variant="line" />
          <CChartLine v-else-if="trendChartData.datasets.length" :datasets="trendChartData.datasets" :labels="trendChartData.labels" :options="lineOptions" style="height: 280px;" />
          <div v-else class="lb-empty">No trend data yet.</div>
        </div>
      </div>
    </section>

    <section class="lb-section">
      <BaseTable
        title="Full Leaderboard"
        :items="tableRows"
        :columns="tableColumns"
        :loading="isLoading"
        :sortable="false"
        empty-text="No telesales staff found for this organisation."
      >
        <template #cell-rank="{ row }">
          <span :class="['lb-rank-pill', rankPillClass(row.rank)]">#{{ row.rank }}</span>
        </template>
        <template #cell-name="{ row }">
          <div class="lb-name-cell">
            <div
              class="lb-name-cell__avatar"
              :style="!photoUrl(row) ? { background: avatarColor(row) } : null"
            >
              <img
                v-if="photoUrl(row)"
                :src="photoUrl(row)"
                :alt="row.name"
                class="lb-avatar__img"
                @error="onAvatarError(row)"
              >
              <span v-else class="lb-avatar__initials lb-name-cell__initials">{{ initials(row.name) }}</span>
            </div>
            <div class="lb-name-cell__text">
              <div class="lb-name-cell__name">{{ row.name }}</div>
              <div class="lb-name-cell__email">{{ row.email }}</div>
            </div>
          </div>
        </template>
        <template #cell-successful_orders="{ row }">
          <span class="lb-orders-cell">
            {{ row.successful_orders }}
            <span
              v-if="row.care_of_orders > 0"
              class="lb-co-tag"
              :title="`${row.care_of_orders} order${row.care_of_orders === 1 ? '' : 's'} credited via care-of`"
            >c/o {{ row.care_of_orders }}</span>
          </span>
        </template>
        <template #cell-change="{ row }">
          <span v-if="row.rank_change === null" class="lb-change lb-change--new">NEW</span>
          <span v-else-if="row.rank_change === 0" class="lb-change lb-change--flat">—</span>
          <span v-else-if="row.rank_change > 0" class="lb-change lb-change--up">▲ {{ row.rank_change }}</span>
          <span v-else class="lb-change lb-change--down">▼ {{ Math.abs(row.rank_change) }}</span>
        </template>
        <template #cell-return_rate="{ row }">
          <span :class="['lb-return-pill', returnPillClass(row.return_rate)]">{{ formatPercent(row.return_rate) }}</span>
        </template>
        <template #cell-net_revenue="{ row }">
          {{ formatCurrency(row.net_revenue) }}
        </template>
      </BaseTable>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import { CChartBar, CChartLine } from '@coreui/vue-chartjs'
import AppDropdown from '@/components/AppDropdown'
import BaseTable from '@/components/BaseTable'
import { getStoredRoles } from '@/auth/access'

const ADMIN_ROLES = ['admin', 'superadmin']
const PALETTE = ['#16a07a', '#0e5b46', '#0ea5e9', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6']

// Gradient palette used to colour-code initials-only avatars. Matches
// the pattern used by client/src/views/users/Users.vue so leaderboard
// avatars are visually consistent with the rest of the app.
const AVATAR_PALETTE = [
  'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
  'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
  'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
  'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
  'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
  'linear-gradient(135deg, #115e59 0%, #14b8a6 100%)'
]

// Lightweight inline-SVG skeleton for the chart cards. Same fade-pulse
// rhythm as the bar/podium skeletons, but shaped like the chart it
// stands in for so the page doesn't visually jump when the real chart
// mounts.
const ChartSkeleton = {
  name: 'ChartSkeleton',
  functional: true,
  props: {
    variant: { type: String, default: 'bar' }
  },
  render (h, { props }) {
    const wrapper = (children) => h('div', { class: 'lb-chart-skeleton' }, [
      h('div', { class: 'lb-chart-skeleton__plot' }, children),
      h('div', { class: 'lb-chart-skeleton__axis' }, [
        h('div', { class: 'lb-skeleton-bar lb-skeleton-bar--xs lb-shimmer' }),
        h('div', { class: 'lb-skeleton-bar lb-skeleton-bar--xs lb-shimmer' }),
        h('div', { class: 'lb-skeleton-bar lb-skeleton-bar--xs lb-shimmer' }),
        h('div', { class: 'lb-skeleton-bar lb-skeleton-bar--xs lb-shimmer' })
      ])
    ])

    if (props.variant === 'line') {
      return wrapper([
        h('svg', {
          attrs: { viewBox: '0 0 320 200', preserveAspectRatio: 'none', width: '100%', height: '100%' },
          class: 'lb-chart-skeleton__svg'
        }, [
          h('path', { attrs: { d: 'M0,140 C40,90 80,160 120,100 S200,40 240,80 S320,60 320,120 L320,200 L0,200 Z', fill: 'currentColor', 'fill-opacity': '0.18' } }),
          h('path', { attrs: { d: 'M0,140 C40,90 80,160 120,100 S200,40 240,80 S320,60 320,120', fill: 'none', stroke: 'currentColor', 'stroke-opacity': '0.55', 'stroke-width': '3', 'stroke-linecap': 'round' } })
        ])
      ])
    }

    const bars = [62, 38, 78, 28, 56, 42, 70, 34].map((h2, i) => h('div', {
      class: 'lb-chart-skeleton__bar lb-shimmer',
      style: { height: `${h2}%`, animationDelay: `${i * 0.08}s` }
    }))
    return wrapper(bars)
  }
}

export default {
  name: 'Leaderboard',
  components: { AppDropdown, BaseTable, CChartBar, CChartLine, ChartSkeleton },
  data () {
    return {
      isLoading: false,
      hasLoadedOnce: false,
      errorMessage: '',
      activePeriod: 'week',
      selectedOrganisationId: null,
      leaderboard: [],
      employeeOfTheWeek: null,
      employeeOfTheMonth: null,
      window: {},
      trend: { labels: [], series: [] },
      brokenAvatars: {},
      periodTabs: [
        { key: 'week', label: 'Last Week' },
        { key: 'month', label: 'Last Month' }
      ]
    }
  },
  computed: {
    ...mapState(['organisations', 'activeOrganisationId']),
    showInitialLoading () {
      return this.isLoading && !this.hasLoadedOnce
    },
    canSwitchOrg () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.some((r) => ADMIN_ROLES.includes(r))
    },
    organisationOptions () {
      return (this.organisations || []).map((org) => ({ value: org.id, label: org.name }))
    },
    organisationLabel () {
      const id = this.selectedOrganisationId || this.activeOrganisationId
      const match = (this.organisations || []).find((org) => org.id === id)
      return match ? match.name : ''
    },
    podium () {
      return this.leaderboard.slice(0, 3)
    },
    tableRows () {
      return this.leaderboard
    },
    tableColumns () {
      return [
        { key: 'rank', label: 'Rank', width: '80px', align: 'center' },
        { key: 'name', label: 'Staff' },
        { key: 'score', label: 'Score', align: 'right', width: '90px' },
        { key: 'successful_orders', label: 'Sales', align: 'right', width: '90px' },
        { key: 'returned_orders', label: 'Returns', align: 'right', width: '100px' },
        { key: 'return_rate', label: 'Return rate', align: 'right', width: '120px' },
        { key: 'bottles_sold', label: 'Bottles', align: 'right', width: '100px' },
        { key: 'net_revenue', label: 'Net revenue', align: 'right', width: '140px' },
        { key: 'change', label: 'vs. last', align: 'center', width: '90px' }
      ]
    },
    scoreChartData () {
      const top = this.leaderboard.slice(0, 8)
      return {
        labels: top.map((row) => row.name),
        datasets: [
          {
            label: 'Score',
            backgroundColor: top.map((_, i) => PALETTE[i % PALETTE.length]),
            data: top.map((row) => row.score)
          }
        ]
      }
    },
    trendChartData () {
      const labels = this.trend.labels || []
      const series = this.trend.series || []
      return {
        labels,
        datasets: series.map((s, i) => ({
          label: s.name,
          backgroundColor: 'transparent',
          borderColor: PALETTE[i % PALETTE.length],
          pointBackgroundColor: PALETTE[i % PALETTE.length],
          pointBorderColor: '#ffffff',
          borderWidth: 2.5,
          tension: 0.35,
          data: s.data || []
        }))
      }
    },
    barOptions () {
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#475569' } }],
          yAxes: [{ ticks: { beginAtZero: true, fontColor: '#475569' }, gridLines: { color: '#eef1f7' } }]
        }
      }
    },
    lineOptions () {
      return {
        maintainAspectRatio: false,
        responsive: true,
        legend: { position: 'bottom', labels: { fontColor: '#475569', usePointStyle: true } },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#475569' } }],
          yAxes: [{ ticks: { beginAtZero: true, fontColor: '#475569' }, gridLines: { color: '#eef1f7' } }]
        }
      }
    }
  },
  watch: {
    activeOrganisationId (newId) {
      if (this.canSwitchOrg) return
      this.selectedOrganisationId = newId
      this.fetchLeaderboard()
    }
  },
  created () {
    this.selectedOrganisationId = this.activeOrganisationId
    this.fetchLeaderboard()
  },
  methods: {
    onPeriodChange (period) {
      if (this.activePeriod === period) return
      this.activePeriod = period
      this.fetchLeaderboard()
    },
    onOrgChange () {
      this.fetchLeaderboard()
    },
    async fetchLeaderboard () {
      this.isLoading = true
      this.errorMessage = ''
      try {
        const payload = { period: this.activePeriod }
        if (this.canSwitchOrg && this.selectedOrganisationId) {
          payload.organisation_id = this.selectedOrganisationId
        }
        const response = await axios.post(`${this.$apiAdress}/api/leaderboard/telesales`, payload)
        const data = response.data || {}
        this.leaderboard = Array.isArray(data.leaderboard) ? data.leaderboard : []
        this.employeeOfTheWeek = data.employee_of_the_week || null
        this.employeeOfTheMonth = data.employee_of_the_month || null
        this.window = data.window || {}
        this.trend = data.trend || { labels: [], series: [] }
        this.hasLoadedOnce = true
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to load the leaderboard right now.'
      } finally {
        this.isLoading = false
      }
    },
    initials (name) {
      if (!name) return '?'
      const parts = String(name).trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + last).toUpperCase() || '?'
    },
    photoUrl (person) {
      if (!person) return ''
      const key = person.user_id != null ? person.user_id : person.id
      if (key != null && this.brokenAvatars[key]) return ''
      const candidates = [
        person.profile_photo_url,
        person.profile_photo,
        person.image,
        person.avatar_url,
        person.avatar,
        person.photo_url,
        person.photo
      ]
      for (const candidate of candidates) {
        if (candidate && typeof candidate === 'string') return candidate
      }
      return ''
    },
    onAvatarError (person) {
      if (!person) return
      const key = person.user_id != null ? person.user_id : person.id
      if (key == null) return
      this.$set(this.brokenAvatars, key, true)
    },
    avatarColor (person) {
      const seed = String(
        (person && (person.name || person.user_id || person.id)) || ''
      )
      let hash = 0
      for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      }
      return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
    },
    rankPillClass (rank) {
      if (rank === 1) return 'lb-rank-pill--gold'
      if (rank === 2) return 'lb-rank-pill--silver'
      if (rank === 3) return 'lb-rank-pill--bronze'
      return 'lb-rank-pill--neutral'
    },
    returnPillClass (rate) {
      const numeric = Number(rate || 0)
      if (numeric === 0) return 'lb-return-pill--good'
      if (numeric < 10) return 'lb-return-pill--ok'
      if (numeric < 25) return 'lb-return-pill--warn'
      return 'lb-return-pill--bad'
    },
    formatRange (range) {
      if (!range || !range.from || !range.to) return ''
      const from = this.formatDate(range.from)
      const to = this.formatDate(range.to)
      return from === to ? from : `${from} – ${to}`
    },
    formatDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    },
    formatPercent (value) {
      const numeric = Number(value || 0)
      return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}%`
    },
    formatCurrency (value) {
      const numeric = Number(value || 0)
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(numeric)
    }
  }
}
</script>

<style scoped>
.lb-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0.25rem 2rem;
}

.lb-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.lb-toolbar__left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.lb-accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.lb-title {
  font-size: 1.15rem;
  color: #1f2a44;
  margin: 0;
  font-weight: 700;
}

.lb-subtitle {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.lb-subtitle__org {
  color: #0e5b46;
  font-weight: 600;
}

.lb-toolbar__right {
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
}

.lb-org-switch {
  display: flex;
  flex-direction: column;
  min-width: 220px;
}

.lb-org-switch__label {
  font-size: 0.72rem;
  color: #4f5560;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.lb-refresh {
  height: 36px;
  width: 36px;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1f2a44;
  cursor: pointer;
  transition: all 0.18s ease;
}

.lb-refresh:hover {
  background: #f8fafc;
  border-color: #16a07a;
  color: #0e5b46;
}

.lb-refresh:focus { outline: none; }
.lb-refresh::-moz-focus-inner { border: 0; }
.lb-refresh:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.lb-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.lb-refresh__icon {
  transition: transform 0.4s ease;
}

.lb-refresh:hover .lb-refresh__icon:not(.is-spinning) {
  transform: rotate(60deg);
}

.lb-refresh__icon.is-spinning {
  animation: lb-spin 1s linear infinite;
}

@keyframes lb-spin {
  to { transform: rotate(360deg); }
}

.lb-alert { margin: 0; }

.lb-hero {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

@media (max-width: 768px) {
  .lb-hero { grid-template-columns: 1fr; }
}

.lb-hero-card {
  position: relative;
  border-radius: 16px;
  padding: 1.4rem 1.4rem 1.5rem;
  color: #ffffff;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(14, 91, 70, 0.18);
}

.lb-hero-card--week {
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(255, 255, 255, 0.18), transparent 55%),
    linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.lb-hero-card--month {
  background:
    radial-gradient(120% 140% at 100% 0%, rgba(255, 255, 255, 0.16), transparent 55%),
    linear-gradient(135deg, #7c2d12 0%, #f59e0b 100%);
  box-shadow: 0 12px 28px rgba(180, 83, 9, 0.22);
}

.lb-hero-card::before {
  content: '';
  position: absolute;
  top: -40px;
  right: -40px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
}

.lb-hero-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.32rem 0.7rem;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin-bottom: 1rem;
  text-transform: uppercase;
}

.lb-hero-card__body {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.lb-hero-card__avatar {
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  border: 3px solid rgba(255, 255, 255, 0.55);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18);
}

.lb-hero-card__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lb-hero-card__crown {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.3rem;
  color: #fde68a;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

.lb-hero-card__meta {
  flex: 1;
  min-width: 0;
}

.lb-hero-card__name {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.2rem;
}

.lb-hero-card__window {
  font-size: 0.75rem;
  opacity: 0.85;
  margin-bottom: 0.6rem;
}

.lb-hero-card__stats {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.lb-stat {
  display: flex;
  flex-direction: column;
}

.lb-stat__value {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.1;
}

.lb-stat__label {
  font-size: 0.65rem;
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.lb-hero-card.is-empty {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #475569;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.lb-hero-card.is-empty .lb-hero-card__badge {
  background: #ffffff;
  color: #475569;
}

.lb-hero-card__empty {
  font-size: 0.85rem;
  padding: 1rem 0;
}

.lb-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  flex-wrap: wrap;
}

.lb-tabs {
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.25rem;
  background: #f8fafc;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
}

.lb-tab {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.1rem;
  border: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
}

.lb-tab:focus { outline: none; }
.lb-tab::-moz-focus-inner { border: 0; }
.lb-tab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.lb-tab:hover:not(.is-active) { background: #f1f5f9; color: #334155; }

.lb-tab.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}

.lb-window {
  font-size: 0.78rem;
  color: #6b7280;
}

.lb-podium {
  display: grid;
  grid-template-columns: 1fr 1.1fr 1fr;
  gap: 0.85rem;
  align-items: end;
}

@media (max-width: 720px) {
  .lb-podium { grid-template-columns: 1fr; }
}

.lb-podium__slot {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  padding: 1rem;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.lb-podium__slot--rank1 {
  padding-top: 2rem;
  padding-bottom: 1.4rem;
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 70%);
  border-color: #fcd34d;
  box-shadow: 0 8px 20px rgba(217, 119, 6, 0.18);
}

.lb-podium__slot--rank2 {
  background: linear-gradient(180deg, #f1f5f9 0%, #ffffff 70%);
  border-color: #cbd5e1;
}

.lb-podium__slot--rank3 {
  background: linear-gradient(180deg, #fef3c7 0%, #ffffff 70%);
  border-color: #fcd1a4;
}

.lb-podium__rank {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  border-radius: 999px;
  padding: 0.18rem 0.7rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.28);
}

.lb-podium__slot--rank1 .lb-podium__rank {
  background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%);
  box-shadow: 0 2px 6px rgba(180, 83, 9, 0.32);
}

.lb-podium__slot--rank2 .lb-podium__rank {
  background: linear-gradient(135deg, #64748b 0%, #94a3b8 100%);
}

.lb-podium__slot--rank3 .lb-podium__rank {
  background: linear-gradient(135deg, #92400e 0%, #d97706 100%);
}

.lb-podium__avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0.4rem auto 0.6rem;
  background: #f1f5f9;
  border: 3px solid #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.lb-podium__avatar--rank1 {
  width: 88px;
  height: 88px;
  border-color: #fde68a;
  box-shadow: 0 6px 16px rgba(217, 119, 6, 0.28);
}

.lb-podium__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lb-podium__crown {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.4rem;
  color: #f59e0b;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

.lb-podium__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
}

.lb-podium__score {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0e5b46;
  line-height: 1;
  margin: 0.4rem 0 0.2rem;
}

.lb-podium__detail {
  font-size: 0.74rem;
  color: #6b7280;
}

.lb-charts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

@media (max-width: 992px) {
  .lb-charts { grid-template-columns: 1fr; }
}

.lb-chart-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.lb-chart-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}

.lb-chart-card__head h3 {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
}

.lb-chart-card__hint {
  font-size: 0.72rem;
  color: #6b7280;
}

.lb-chart-card__body {
  padding: 0.85rem 1rem 1rem;
}

.lb-empty {
  text-align: center;
  font-size: 0.85rem;
  color: #94a3b8;
  padding: 3rem 0;
}

.lb-name-cell {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.lb-name-cell__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eef2f7;
  border: 1px solid #e5e9f2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.lb-name-cell__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lb-name-cell__text { min-width: 0; }

.lb-name-cell__name {
  font-weight: 600;
  color: #1f2a44;
  font-size: 0.88rem;
  line-height: 1.2;
}

.lb-name-cell__email {
  font-size: 0.72rem;
  color: #94a3b8;
}

.lb-rank-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  color: #ffffff;
}

.lb-rank-pill--gold   { background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%); }
.lb-rank-pill--silver { background: linear-gradient(135deg, #64748b 0%, #94a3b8 100%); }
.lb-rank-pill--bronze { background: linear-gradient(135deg, #92400e 0%, #d97706 100%); }
.lb-rank-pill--neutral{ background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%); }

.lb-return-pill {
  display: inline-block;
  padding: 0.16rem 0.5rem;
  border-radius: 6px;
  font-size: 0.74rem;
  font-weight: 600;
}

.lb-return-pill--good { background: #dcfce7; color: #166534; }
.lb-return-pill--ok   { background: #ecfdf5; color: #047857; }
.lb-return-pill--warn { background: #fef3c7; color: #92400e; }
.lb-return-pill--bad  { background: #fee2e2; color: #991b1b; }

.lb-change {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 0.16rem 0.45rem;
  border-radius: 6px;
}

.lb-change--up   { background: #dcfce7; color: #166534; }
.lb-change--down { background: #fee2e2; color: #991b1b; }
.lb-change--flat { color: #94a3b8; }
.lb-change--new  { background: #ede9fe; color: #5b21b6; }

.lb-orders-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  justify-content: flex-end;
  width: 100%;
}

.lb-co-tag {
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0.08rem 0.4rem;
  border-radius: 4px;
  background: #ecfeff;
  color: #0e7490;
  border: 1px solid #cffafe;
  white-space: nowrap;
}

/* ===== Avatar (image / initials) ===== */

.lb-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.lb-avatar__initials {
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #ffffff;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(15, 23, 42, 0.18);
}

.lb-hero-card__avatar .lb-avatar__initials { font-size: 1.6rem; }
.lb-podium__avatar    .lb-podium__initials { font-size: 1.35rem; }
.lb-podium__avatar--rank1 .lb-podium__initials { font-size: 1.55rem; }
.lb-name-cell__avatar .lb-name-cell__initials { font-size: 0.78rem; }

/* ===== Skeleton placeholders ===== */

.lb-shimmer {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: lb-shimmer 1.4s ease-in-out infinite;
}

.lb-shimmer-on-dark {
  background: linear-gradient(90deg,
    rgba(255, 255, 255, 0.18) 25%,
    rgba(255, 255, 255, 0.34) 50%,
    rgba(255, 255, 255, 0.18) 75%);
  background-size: 200% 100%;
  animation: lb-shimmer 1.4s ease-in-out infinite;
}

@keyframes lb-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.lb-skeleton-bar {
  border-radius: 6px;
  width: 100%;
}

.lb-skeleton-bar--xs { height: 8px;  width: 60%; margin: 0.18rem 0; }
.lb-skeleton-bar--sm { height: 10px; width: 50%; margin: 0.25rem 0; }
.lb-skeleton-bar--md { height: 14px; width: 70%; margin: 0.3rem 0; }
.lb-skeleton-bar--xl { height: 18px; width: 80%; margin: 0.35rem 0; }

.lb-hero-card__avatar--skeleton {
  background: rgba(255, 255, 255, 0.18) !important;
  border-color: rgba(255, 255, 255, 0.32);
}

.lb-podium__slot--skeleton {
  pointer-events: none;
}

.lb-podium__rank--skeleton {
  width: 48px;
  height: 18px;
  border-radius: 999px;
  background: linear-gradient(90deg,
    rgba(255, 255, 255, 0.55) 25%,
    rgba(255, 255, 255, 0.85) 50%,
    rgba(255, 255, 255, 0.55) 75%);
  background-size: 200% 100%;
  animation: lb-shimmer 1.4s ease-in-out infinite;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.12);
  padding: 0;
}

.lb-podium__avatar--skeleton {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%) !important;
  background-size: 200% 100% !important;
  animation: lb-shimmer 1.4s ease-in-out infinite;
  border-color: #ffffff;
}

.lb-podium__sk-name   { margin: 0.4rem auto 0.3rem; width: 70%; }
.lb-podium__sk-score  { margin: 0.2rem auto;        width: 50%; height: 22px; }
.lb-podium__sk-detail { margin: 0.4rem auto 0;      width: 80%; height: 8px; }

.lb-chart-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  height: 280px;
  padding: 0.4rem 0.2rem 0;
}

.lb-chart-skeleton__plot {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
  padding: 0 0.4rem;
  color: #16a07a;
}

.lb-chart-skeleton__bar {
  flex: 1;
  border-radius: 6px 6px 0 0;
  min-width: 0;
}

.lb-chart-skeleton__svg {
  width: 100%;
  height: 100%;
  color: #16a07a;
  display: block;
}

.lb-chart-skeleton__axis {
  display: flex;
  gap: 0.6rem;
  padding: 0 0.4rem;
}

.lb-chart-skeleton__axis .lb-skeleton-bar {
  flex: 1;
  margin: 0;
}
</style>
